import { useEffect, useMemo, useState } from "react";
import { useApi, apiGet, apiPatch, apiPost, apiDelete, apiPut, API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingBlock, ErrorState, EmptyState } from "@/components/common/StateViews";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { BarCard, DonutCard, AreaCard } from "@/components/common/Charts";
import { ChartInsight } from "@/components/common/ChartInsight";
import { fmtCurrency, fmtNum } from "@/lib/format";
import { toast } from "sonner";
import { Search, Download, FileText, ShieldCheck, ShieldAlert, Lock, Eye, EyeOff, ScanEye, Bell, Trash2, Plus, TriangleAlert, Wallet, Users, TrendingUp, Network, History } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const TIER_BY_ROLE = (rk) => {
  const full = ["platform_owner", "super_admin", "security_admin"];
  const redact = [...full, "compliance_auditor", "secops_analyst", "content_reviewer"];
  if (full.includes(rk)) return 2;
  if (redact.includes(rk)) return 1;
  return 0;
};
const TIERS = [
  { key: "metadata", label: "Metadata", icon: EyeOff, tier: 0 },
  { key: "redacted", label: "Redacted", icon: ScanEye, tier: 1 },
  { key: "full", label: "Full content", icon: Eye, tier: 2 },
];
const DECISION = {
  allow: { label: "Allowed", cls: "text-[hsl(var(--sev-success))] border-[hsl(var(--sev-success)/0.4)] bg-[hsl(var(--sev-success)/0.08)]" },
  block: { label: "Blocked", cls: "text-[hsl(var(--sev-danger))] border-[hsl(var(--sev-danger)/0.4)] bg-[hsl(var(--sev-danger)/0.08)]" },
  redact: { label: "Redacted", cls: "text-[hsl(var(--sev-warning))] border-[hsl(var(--sev-warning)/0.4)] bg-[hsl(var(--sev-warning)/0.08)]" },
  coach: { label: "Coached", cls: "text-[hsl(var(--sev-info))] border-[hsl(var(--sev-info)/0.4)] bg-[hsl(var(--sev-info)/0.08)]" },
};
const METRIC_LABEL = { spend: "AI spend (USD)", blocked: "Blocked actions", sensitive: "Sensitive events" };

function DecisionBadge({ d }) {
  const m = DECISION[d] || DECISION.allow;
  return <Badge variant="outline" className={`text-[10px] font-medium ${m.cls}`} data-testid={`decision-${d}`}>{m.label}</Badge>;
}

export default function People() {
  const { user } = useApp();
  const roleKey = user?.role_key || "employee";
  const maxTier = TIER_BY_ROLE(roleKey);

  const [dept, setDept] = useState("all");
  const [q, setQ] = useState("");
  const { data: people, isLoading, error, refetch: reloadPeople } = useApi(["people", dept], `/people${dept !== "all" ? `?dept=${encodeURIComponent(dept)}` : ""}`);
  const { data: depts, refetch: reloadDepts } = useApi("people-depts", "/people/departments");
  const { data: alertsData, refetch: reloadAlerts } = useApi("people-alerts", "/people/alerts");
  const { data: teams } = useApi("people-teams", "/people/teams");
  const { data: anomalies } = useApi("people-anomalies", "/people/anomalies");
  const { data: audit, refetch: reloadAudit } = useApi("people-audit", "/people/reveal-audit");
  const actorId = user?.id || roleKey;
  const actorName = user?.name || roleKey;

  const [openOid, setOpenOid] = useState(null);

  const allDepts = useMemo(() => (depts || []).map((d) => d.dept), [depts]);
  const filtered = useMemo(() => (people || []).filter((p) => p.name.toLowerCase().includes(q.toLowerCase())), [people, q]);
  const kpis = useMemo(() => {
    const list = people || [];
    return {
      spend: list.reduce((s, p) => s + p.spend, 0),
      blocked: list.reduce((s, p) => s + p.blocked, 0),
      sensitive: list.reduce((s, p) => s + p.sensitive, 0),
      over: list.filter((p) => p.over_budget).length,
    };
  }, [people]);

  if (error) return <ErrorState />;

  return (
    <div>
      <PageHeader eyebrow="Navitra Lens · Governance" title="Workforce Intelligence" subtitle="Enterprise-wide AI usage, drilled down to every employee — what they ran, what was allowed or blocked, spend, budgets and alerts.">
        <Badge variant="outline" className="gap-1.5 text-[11px]" data-testid="privacy-tier-badge">
          <Lock className="h-3 w-3" />Your access: <span className="font-semibold capitalize">{["Metadata", "Redacted", "Full content"][maxTier]}</span>
        </Badge>
      </PageHeader>

      <Tabs defaultValue="employees">
        <TabsList data-testid="workforce-tabs">
          <TabsTrigger value="employees" data-testid="tab-employees"><Users className="h-3.5 w-3.5 mr-1.5" />Employees</TabsTrigger>
          <TabsTrigger value="departments" data-testid="tab-departments"><Wallet className="h-3.5 w-3.5 mr-1.5" />Departments &amp; budgets</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts"><Bell className="h-3.5 w-3.5 mr-1.5" />Usage alerts{alertsData?.firing_count ? <span className="ml-1.5 rounded-full bg-[hsl(var(--sev-danger))] text-white text-[10px] px-1.5">{alertsData.firing_count}</span> : null}</TabsTrigger>
          <TabsTrigger value="teams" data-testid="tab-teams"><Network className="h-3.5 w-3.5 mr-1.5" />Teams</TabsTrigger>
          <TabsTrigger value="anomalies" data-testid="tab-anomalies"><TrendingUp className="h-3.5 w-3.5 mr-1.5" />Anomalies{anomalies?.anomalies?.length ? <span className="ml-1.5 rounded-full bg-gold text-white text-[10px] px-1.5">{anomalies.anomalies.length}</span> : null}</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><History className="h-3.5 w-3.5 mr-1.5" />Reveal audit</TabsTrigger>
        </TabsList>

        {/* ============ EMPLOYEES ============ */}
        <TabsContent value="employees" className="space-y-4 pt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[["Total AI spend", fmtCurrency(kpis.spend), Wallet, "text-navy"],
              ["Blocked actions", fmtNum(kpis.blocked), ShieldAlert, "text-[hsl(var(--sev-danger))]"],
              ["Sensitive events", fmtNum(kpis.sensitive), ScanEye, "text-[hsl(var(--sev-warning))]"],
              ["Over budget", `${kpis.over} people`, TriangleAlert, "text-gold"]].map(([l, v, Icon, c]) => (
              <Card key={l}><CardContent className="p-4"><div className="flex items-center justify-between"><p className="text-[11px] uppercase tracking-wide text-muted-foreground">{l}</p><Icon className={`h-4 w-4 ${c}`} /></div><p className="font-display text-2xl mt-1 tabular-nums">{v}</p></CardContent></Card>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employees…" className="pl-9" data-testid="people-search" /></div>
            <Select value={dept} onValueChange={setDept}>
              <SelectTrigger className="w-[190px]" data-testid="dept-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All departments</SelectItem>{allDepts.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {isLoading || !people ? <LoadingBlock /> : (
            <>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="font-display text-base">Top AI spenders</CardTitle></CardHeader>
                <CardContent>
                  <ChartInsight title="Top AI spenders" app="Workforce Intelligence"
                    context={`The top ${Math.min(8, filtered.length)} employees by governed AI spend. Watch for concentration and over-budget users.`}
                    data={filtered.slice(0, 8).map((p) => ({ label: p.name, value: p.spend }))}>
                    <BarCard data={filtered.slice(0, 8).map((p) => ({ name: p.name.split(" ")[0], usd: Math.round(p.spend) }))} xKey="name" yKey="usd" height={220} fmt={(v) => fmtCurrency(v)} />
                  </ChartInsight>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="font-display text-base">Employees ({filtered.length})</CardTitle></CardHeader>
                <CardContent className="p-0">
                  {filtered.length === 0 ? <div className="p-6"><EmptyState title="No employees match" hint="Try a different search or department." /></div> : (
                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Employee</TableHead><TableHead>Dept</TableHead>
                        <TableHead className="text-right">Interactions</TableHead><TableHead className="text-right">Spend</TableHead>
                        <TableHead className="text-right">Blocked</TableHead><TableHead className="text-right">Sensitive</TableHead>
                        <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {filtered.map((p) => (
                          <TableRow key={p.oid} data-testid={`person-row-${p.oid}`} className="cursor-pointer" onClick={() => setOpenOid(p.oid)}>
                            <TableCell className="font-medium">{p.name}</TableCell>
                            <TableCell className="text-muted-foreground">{p.dept}</TableCell>
                            <TableCell className="text-right mono">{fmtNum(p.events)}</TableCell>
                            <TableCell className="text-right mono">{fmtCurrency(p.spend)}</TableCell>
                            <TableCell className="text-right mono">{fmtNum(p.blocked)}</TableCell>
                            <TableCell className="text-right mono">{fmtNum(p.sensitive)}</TableCell>
                            <TableCell>
                              <div className="flex gap-1.5">
                                {p.over_budget && <Badge variant="outline" className="text-[10px] text-gold border-gold/40 bg-gold/10">Over budget</Badge>}
                                {p.firing && <Badge variant="outline" className="text-[10px] text-[hsl(var(--sev-danger))] border-[hsl(var(--sev-danger)/0.4)] bg-[hsl(var(--sev-danger)/0.08)] gap-1"><Bell className="h-3 w-3" />Alert</Badge>}
                                {!p.over_budget && !p.firing && <span className="text-[11px] text-muted-foreground flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--sev-success))]" />Normal</span>}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setOpenOid(p.oid); }} data-testid={`view-${p.oid}`}>Deep dive</Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ============ DEPARTMENTS ============ */}
        <TabsContent value="departments" className="space-y-4 pt-4">
          {!depts ? <LoadingBlock /> : (
            <>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="font-display text-base">AI spend by department</CardTitle></CardHeader>
                <CardContent>
                  <ChartInsight title="AI spend by department" app="Workforce Intelligence"
                    context="Departmental concentration of governed AI spend. Use this to set fair budgets and chargeback."
                    data={depts.map((d) => ({ label: d.dept, value: d.spend }))}>
                    <BarCard data={depts.map((d) => ({ name: d.dept, usd: Math.round(d.spend) }))} xKey="name" yKey="usd" height={230} fmt={(v) => fmtCurrency(v)} />
                  </ChartInsight>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="font-display text-base">Department budgets</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader><TableRow><TableHead>Department</TableHead><TableHead className="text-right">People</TableHead><TableHead className="text-right">Spend</TableHead><TableHead className="text-right">Blocked</TableHead><TableHead>Monthly budget</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {depts.map((d) => <DeptRow key={d.dept} d={d} onSaved={() => { reloadDepts(); }} />)}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ============ ALERTS ============ */}
        <TabsContent value="alerts" className="space-y-4 pt-4">
          <div className="rounded-lg border border-[hsl(var(--sev-info)/0.35)] bg-[hsl(var(--sev-info)/0.06)] p-3 flex items-start gap-2 text-sm" data-testid="alerts-banner">
            <Bell className="h-4 w-4 text-[hsl(var(--sev-info))] mt-0.5 shrink-0" />
            <p className="text-muted-foreground"><span className="font-medium text-foreground">Quiet monitoring.</span> Usage alerts are evaluated here for admins only — they deliberately do <span className="font-medium text-foreground">not</span> appear in the desktop notification bell. Create per-employee alert rules from any employee&rsquo;s deep-dive.</p>
          </div>
          {!alertsData ? <LoadingBlock /> : alertsData.alerts.length === 0 ? (
            <EmptyState title="No alert rules yet" hint="Open an employee's deep dive to create a usage alert rule." icon={Bell} />
          ) : (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="font-display text-base flex items-center gap-2">Alert rules<Badge variant="outline" className="text-[10px]">{alertsData.firing_count} firing / {alertsData.rules_count}</Badge></CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Dept</TableHead><TableHead>Rule</TableHead><TableHead className="text-right">Current</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {alertsData.alerts.map((r) => (
                      <TableRow key={r.rule_id} data-testid={`alert-row-${r.rule_id}`}>
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell className="text-muted-foreground">{r.dept}</TableCell>
                        <TableCell className="text-sm">{METRIC_LABEL[r.metric] || r.metric} <span className="mono text-muted-foreground">{r.op} {r.metric === "spend" ? fmtCurrency(r.threshold) : fmtNum(r.threshold)}</span></TableCell>
                        <TableCell className="text-right mono">{r.metric === "spend" ? fmtCurrency(r.current) : fmtNum(r.current)}</TableCell>
                        <TableCell>{r.firing ? <Badge variant="outline" className="text-[10px] text-[hsl(var(--sev-danger))] border-[hsl(var(--sev-danger)/0.4)] bg-[hsl(var(--sev-danger)/0.08)] gap-1"><TriangleAlert className="h-3 w-3" />Firing</Badge> : <span className="text-[11px] text-muted-foreground">Normal</span>}</TableCell>
                        <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => setOpenOid(r.oid)} data-testid={`alert-open-${r.oid}`}>Manage</Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ============ TEAMS ============ */}
        <TabsContent value="teams" className="space-y-4 pt-4">
          {!teams ? <LoadingBlock /> : (
            <>
              <Card><CardHeader className="pb-2"><CardTitle className="font-display text-base">AI spend by reporting line</CardTitle></CardHeader><CardContent>
                <ChartInsight title="AI spend by manager" app="Workforce Intelligence" context="Governed AI spend rolled up under each manager's reporting line."
                  data={teams.map((t) => ({ label: t.manager, value: t.spend }))}>
                  <BarCard data={teams.map((t) => ({ name: t.manager, usd: Math.round(t.spend) }))} xKey="name" yKey="usd" height={220} fmt={(v) => fmtCurrency(v)} />
                </ChartInsight>
              </CardContent></Card>
              <Accordion type="single" collapsible className="space-y-2">
                {teams.map((t) => (
                  <AccordionItem key={t.manager} value={t.manager} className="border rounded-lg px-3" data-testid={`team-${t.manager}`}>
                    <AccordionTrigger className="hover:no-underline py-3">
                      <div className="flex-1 flex items-center justify-between pr-3 text-left">
                        <span className="font-medium">{t.manager}<span className="ml-2 text-[11px] text-muted-foreground">{fmtNum(t.people)} people</span></span>
                        <span className="flex gap-3 text-sm mono"><span>{fmtCurrency(t.spend)}</span><span className="text-[hsl(var(--sev-danger))]">{fmtNum(t.blocked)} blocked</span></span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <Table><TableHeader><TableRow><TableHead>Member</TableHead><TableHead>Dept</TableHead><TableHead className="text-right">Spend</TableHead><TableHead className="text-right">Blocked</TableHead><TableHead></TableHead></TableRow></TableHeader>
                        <TableBody>{t.members.map((m) => (
                          <TableRow key={m.oid}><TableCell className="font-medium">{m.name}</TableCell><TableCell className="text-muted-foreground">{m.dept}</TableCell><TableCell className="text-right mono">{fmtCurrency(m.spend)}</TableCell><TableCell className="text-right mono">{fmtNum(m.blocked)}</TableCell><TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => setOpenOid(m.oid)} data-testid={`team-view-${m.oid}`}>Open</Button></TableCell></TableRow>
                        ))}</TableBody>
                      </Table>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </>
          )}
        </TabsContent>

        {/* ============ ANOMALIES ============ */}
        <TabsContent value="anomalies" className="space-y-4 pt-4">
          <div className="rounded-lg border border-gold/30 bg-gold/[0.06] p-3 flex items-start gap-2 text-sm" data-testid="anomalies-banner">
            <TrendingUp className="h-4 w-4 text-gold mt-0.5 shrink-0" />
            <p className="text-muted-foreground"><span className="font-medium text-foreground">Spike detection.</span> Each employee is compared to <span className="font-medium text-foreground">their own</span> weekly spend baseline. We surface people whose latest week is unusually high{anomalies?.window ? ` (window ${anomalies.window})` : ""}.</p>
          </div>
          {!anomalies ? <LoadingBlock /> : anomalies.anomalies.length === 0 ? (
            <EmptyState title="No anomalies detected" hint="Everyone is tracking close to their usual usage." icon={ShieldCheck} />
          ) : (
            <Card><CardContent className="p-0"><Table>
              <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Dept</TableHead><TableHead className="text-right">Weekly baseline</TableHead><TableHead className="text-right">Latest week</TableHead><TableHead className="text-right">Spike</TableHead><TableHead>Severity</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>{anomalies.anomalies.map((a) => {
                const sc = { high: "text-[hsl(var(--sev-danger))] border-[hsl(var(--sev-danger)/0.4)] bg-[hsl(var(--sev-danger)/0.08)]", medium: "text-gold border-gold/40 bg-gold/10", low: "text-[hsl(var(--sev-info))] border-[hsl(var(--sev-info)/0.4)] bg-[hsl(var(--sev-info)/0.08)]" }[a.severity];
                return (<TableRow key={a.oid} data-testid={`anomaly-${a.oid}`}><TableCell className="font-medium">{a.name}</TableCell><TableCell className="text-muted-foreground">{a.dept}</TableCell><TableCell className="text-right mono">{fmtCurrency(a.baseline)}</TableCell><TableCell className="text-right mono">{fmtCurrency(a.recent)}</TableCell><TableCell className="text-right mono font-semibold">{a.ratio}x</TableCell><TableCell><Badge variant="outline" className={`text-[10px] capitalize ${sc}`}>{a.severity}</Badge></TableCell><TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => setOpenOid(a.oid)} data-testid={`anomaly-view-${a.oid}`}>Deep dive</Button></TableCell></TableRow>);
              })}</TableBody>
            </Table></CardContent></Card>
          )}
        </TabsContent>

        {/* ============ REVEAL AUDIT ============ */}
        <TabsContent value="audit" className="space-y-4 pt-4">
          <div className="rounded-lg border border-[hsl(var(--sev-info)/0.35)] bg-[hsl(var(--sev-info)/0.06)] p-3 flex items-start gap-2 text-sm" data-testid="audit-banner">
            <History className="h-4 w-4 text-[hsl(var(--sev-info))] mt-0.5 shrink-0" />
            <p className="text-muted-foreground"><span className="font-medium text-foreground">Access is auditable.</span> Every time an admin reveals redacted snippets or full prompt content, it is logged here with who, when and at what privacy tier.</p>
          </div>
          {!audit ? <LoadingBlock /> : audit.audit.length === 0 ? (
            <EmptyState title="No reveals yet" hint="Redacted or full-content views will appear here." icon={ScanEye} />
          ) : (
            <Card><CardHeader className="pb-2"><CardTitle className="font-display text-base flex items-center gap-2">Reveal log <Badge variant="outline" className="text-[10px]">{audit.total} total</Badge></CardTitle></CardHeader><CardContent className="p-0"><Table>
              <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Viewed by</TableHead><TableHead>Role</TableHead><TableHead>Employee</TableHead><TableHead>Tier</TableHead></TableRow></TableHeader>
              <TableBody>{audit.audit.map((r) => (
                <TableRow key={r.id} data-testid={`audit-${r.id}`}><TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">{new Date(r.ts).toLocaleString()}</TableCell><TableCell className="font-medium">{r.actor_name}</TableCell><TableCell className="text-muted-foreground text-[11px]">{r.actor_role_key}</TableCell><TableCell>{r.subject}</TableCell><TableCell><Badge variant="outline" className={`text-[10px] capitalize ${r.tier === "full" ? "text-[hsl(var(--sev-danger))] border-[hsl(var(--sev-danger)/0.4)] bg-[hsl(var(--sev-danger)/0.08)]" : "text-gold border-gold/40 bg-gold/10"}`}>{r.tier}</Badge></TableCell></TableRow>
              ))}</TableBody>
            </Table></CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      <EmployeeDrawer oid={openOid} maxTier={maxTier} roleKey={roleKey} actorId={actorId} actorName={actorName} onClose={() => setOpenOid(null)}
        onChanged={() => { reloadPeople(); reloadDepts(); reloadAlerts(); reloadAudit(); }} />
    </div>
  );
}

function DeptRow({ d, onSaved }) {
  const [val, setVal] = useState(d.budget_usd ?? "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await apiPut(`/people/departments/${encodeURIComponent(d.dept)}`, { budget_usd: val === "" ? null : Number(val) }); toast.success(`${d.dept} budget saved`); onSaved(); }
    catch { toast.error("Could not save budget"); }
    setSaving(false);
  };
  return (
    <TableRow data-testid={`dept-row-${d.dept}`}>
      <TableCell className="font-medium">{d.dept}</TableCell>
      <TableCell className="text-right mono">{fmtNum(d.people)}</TableCell>
      <TableCell className="text-right mono">{fmtCurrency(d.spend)}{d.over_budget && <span className="ml-1.5 text-gold text-[10px]">over</span>}</TableCell>
      <TableCell className="text-right mono">{fmtNum(d.blocked)}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <Input type="number" value={val} onChange={(e) => setVal(e.target.value)} placeholder="—" className="h-8 w-28" data-testid={`dept-budget-${d.dept}`} />
          <Button size="sm" variant="outline" onClick={save} disabled={saving} data-testid={`dept-save-${d.dept}`}>Save</Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function EmployeeDrawer({ oid, maxTier, roleKey, actorId, actorName, onClose, onChanged }) {
  const [view, setView] = useState("metadata");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [budget, setBudget] = useState("");
  const [rule, setRule] = useState({ metric: "spend", op: ">", threshold: "" });

  const load = async (v = view) => {
    if (!oid) return;
    setLoading(true);
    try {
      const d = await apiGet(`/people/${oid}`, { view: v, actor_role_key: roleKey, actor_id: actorId, actor_name: actorName });
      setData(d); setBudget(d.budget_usd ?? "");
    } catch { toast.error("Could not load employee"); }
    setLoading(false);
  };
  useEffect(() => { if (oid) { setView("metadata"); load("metadata"); } /* eslint-disable-next-line */ }, [oid]);

  const changeView = (v) => { setView(v); load(v); };
  const saveBudget = async () => {
    try { await apiPatch(`/people/${oid}`, { budget_usd: budget === "" ? null : Number(budget) }); toast.success("Budget saved"); onChanged?.(); load(); }
    catch { toast.error("Could not save budget"); }
  };
  const addRule = async () => {
    if (rule.threshold === "" || isNaN(Number(rule.threshold))) return toast.error("Enter a numeric threshold");
    try {
      await apiPost(`/people/${oid}/alerts`, { ...rule, threshold: Number(rule.threshold), label: `${METRIC_LABEL[rule.metric]} ${rule.op} ${rule.threshold}` });
      toast.success("Alert rule created"); setRule({ metric: "spend", op: ">", threshold: "" }); onChanged?.(); load();
    } catch { toast.error("Could not create rule"); }
  };
  const delRule = async (rid) => {
    try { await apiDelete(`/people/${oid}/alerts/${rid}`); toast.success("Rule removed"); onChanged?.(); load(); }
    catch { toast.error("Could not remove rule"); }
  };

  return (
    <Sheet open={!!oid} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto thin-scroll" data-testid="employee-drawer">
        {!data || loading ? <div className="pt-10"><LoadingBlock /></div> : (
          <>
            <SheetHeader className="mb-4">
              <SheetTitle className="font-display flex items-center gap-2">{data.name}
                <Badge variant="outline" className="text-[10px] font-normal">{data.dept}{data.region ? ` · ${data.region}` : ""}</Badge>
              </SheetTitle>
            </SheetHeader>

            {/* KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              {[["Spend", fmtCurrency(data.totals.spend)], ["Interactions", fmtNum(data.totals.events)], ["Blocked", fmtNum(data.totals.blocked)], ["Sensitive", fmtNum(data.totals.sensitive)]].map(([l, v]) => (
                <div key={l} className="rounded-lg border p-2.5"><p className="text-[10px] uppercase tracking-wide text-muted-foreground">{l}</p><p className="font-display text-lg tabular-nums">{v}</p></div>
              ))}
            </div>

            {/* Trend */}
            {data.trend?.length > 1 && (
              <Card className="mb-4"><CardHeader className="pb-1 pt-3"><CardTitle className="text-sm font-display">Spend trend</CardTitle></CardHeader><CardContent>
                <ChartInsight title={`${data.name} — spend trend`} app="Workforce Intelligence" context="Daily governed AI spend for this employee over the recent window."
                  data={data.trend.map((t) => ({ label: t.date, value: t.usd }))}>
                  <AreaCard data={data.trend} xKey="date" yKey="usd" height={170} fmt={(v) => fmtCurrency(v)} />
                </ChartInsight>
              </CardContent></Card>
            )}

            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <Card><CardHeader className="pb-1 pt-3"><CardTitle className="text-sm font-display">By vendor</CardTitle></CardHeader><CardContent>
                <DonutCard data={data.by_vendor} nameKey="label" valueKey="usd" height={180} fmt={(v) => fmtCurrency(v)} />
              </CardContent></Card>
              <Card><CardHeader className="pb-1 pt-3"><CardTitle className="text-sm font-display">Top use cases</CardTitle></CardHeader><CardContent>
                <BarCard data={data.by_use_case.slice(0, 6).map((u) => ({ name: u.label.replace(/_/g, " "), count: u.count }))} xKey="name" yKey="count" height={180} horizontal fmt={(v) => fmtNum(v)} />
              </CardContent></Card>
            </div>

            {/* Sensitive exposure */}
            {data.sensitive?.length > 0 && (
              <Card className="mb-4"><CardHeader className="pb-1 pt-3"><CardTitle className="text-sm font-display flex items-center gap-1.5"><ScanEye className="h-4 w-4 text-[hsl(var(--sev-warning))]" />Sensitive data exposure</CardTitle></CardHeader><CardContent>
                <div className="flex flex-wrap gap-1.5">{data.sensitive.map((s) => <Badge key={s.label} variant="outline" className="text-[11px]">{s.label} · {fmtNum(s.count)}</Badge>)}</div>
              </CardContent></Card>
            )}

            {/* Budget */}
            <Card className="mb-4"><CardHeader className="pb-1 pt-3"><CardTitle className="text-sm font-display flex items-center gap-1.5"><Wallet className="h-4 w-4 text-navy" />Budget allocation</CardTitle></CardHeader><CardContent className="flex items-end gap-2">
              <div className="flex-1"><Label className="text-xs">Monthly budget (USD)</Label><Input type="number" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="No budget set" data-testid="drawer-budget" /></div>
              <Button variant="outline" onClick={saveBudget} data-testid="drawer-budget-save">Save budget</Button>
            </CardContent></Card>

            {/* Alert rules */}
            <Card className="mb-4"><CardHeader className="pb-1 pt-3"><CardTitle className="text-sm font-display flex items-center gap-1.5"><Bell className="h-4 w-4 text-gold" />Usage alert rules <span className="text-[10px] font-normal text-muted-foreground">(quiet · not notifications)</span></CardTitle></CardHeader><CardContent className="space-y-2">
              {(data.alerts || []).length === 0 && <p className="text-xs text-muted-foreground">No alert rules for this employee yet.</p>}
              {(data.alerts || []).map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-md border p-2 text-sm" data-testid={`drawer-rule-${r.id}`}>
                  <span>{METRIC_LABEL[r.metric] || r.metric} <span className="mono text-muted-foreground">{r.op} {r.metric === "spend" ? fmtCurrency(r.threshold) : fmtNum(r.threshold)}</span></span>
                  <Button size="sm" variant="ghost" className="text-[hsl(var(--sev-danger))]" onClick={() => delRule(r.id)} data-testid={`drawer-rule-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              <div className="flex items-end gap-2 pt-1">
                <div className="flex-1"><Label className="text-[11px]">Metric</Label>
                  <Select value={rule.metric} onValueChange={(v) => setRule({ ...rule, metric: v })}><SelectTrigger className="h-8" data-testid="rule-metric"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="spend">AI spend</SelectItem><SelectItem value="blocked">Blocked actions</SelectItem><SelectItem value="sensitive">Sensitive events</SelectItem></SelectContent></Select>
                </div>
                <div className="w-16"><Label className="text-[11px]">Op</Label>
                  <Select value={rule.op} onValueChange={(v) => setRule({ ...rule, op: v })}><SelectTrigger className="h-8" data-testid="rule-op"><SelectValue /></SelectTrigger><SelectContent><SelectItem value=">">&gt;</SelectItem><SelectItem value=">=">&ge;</SelectItem></SelectContent></Select>
                </div>
                <div className="w-28"><Label className="text-[11px]">Threshold</Label><Input type="number" value={rule.threshold} onChange={(e) => setRule({ ...rule, threshold: e.target.value })} className="h-8" data-testid="rule-threshold" /></div>
                <Button size="sm" onClick={addRule} data-testid="rule-add"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
              </div>
            </CardContent></Card>

            {/* Activity + privacy tiers */}
            <Card className="mb-4"><CardHeader className="pb-1 pt-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-sm font-display">Recent activity</CardTitle>
                <div className="flex items-center gap-1 rounded-lg border p-0.5" data-testid="privacy-toggle">
                  {TIERS.map((t) => {
                    const disabled = t.tier > maxTier;
                    const Icon = t.icon;
                    return (
                      <button key={t.key} disabled={disabled} onClick={() => changeView(t.key)} data-testid={`tier-${t.key}`}
                        title={disabled ? "Your role cannot access this level" : `Show ${t.label.toLowerCase()}`}
                        className={`flex items-center gap-1 rounded-md px-2 py-1 text-[11px] transition-colors ${view === t.key ? "bg-navy text-white" : disabled ? "text-muted-foreground/40 cursor-not-allowed" : "hover:bg-muted"}`}>
                        <Icon className="h-3 w-3" />{t.label}{disabled && <Lock className="h-2.5 w-2.5" />}
                      </button>
                    );
                  })}
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Showing <span className="font-medium capitalize">{data.privacy.tier_label}</span>. Prompt content is only revealed to authorised roles.</p>
            </CardHeader><CardContent className="p-0">
              <div className="max-h-[320px] overflow-y-auto thin-scroll">
                <Table>
                  <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Action</TableHead><TableHead>Vendor</TableHead><TableHead>Decision</TableHead>{data.privacy.granted_tier >= 1 && <TableHead>Prompt</TableHead>}<TableHead className="text-right">Cost</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {data.activity.map((a, i) => (
                      <TableRow key={i} data-testid={`activity-${i}`}>
                        <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">{new Date(a.ts).toLocaleDateString()}</TableCell>
                        <TableCell className="text-sm"><span className="font-medium">{a.action}</span><span className="block text-[11px] text-muted-foreground">{a.use_case.replace(/_/g, " ")}</span></TableCell>
                        <TableCell className="text-[11px]">{a.vendor}<span className="block text-muted-foreground">{a.model}</span></TableCell>
                        <TableCell><DecisionBadge d={a.decision} /></TableCell>
                        {data.privacy.granted_tier >= 1 && <TableCell className="text-[11px] max-w-[240px]"><span className="line-clamp-2">{a.prompt}</span>{a.data_classes?.length > 0 && <span className="block mt-0.5 text-[hsl(var(--sev-warning))]">{a.data_classes.join(", ")}</span>}</TableCell>}
                        <TableCell className="text-right mono text-[11px]">{fmtCurrency(a.cost)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent></Card>

            {/* Exports */}
            <div className="flex gap-2 pb-6">
              <Button variant="outline" onClick={() => window.open(`${API}/people/${oid}/report.pdf`, "_blank")} data-testid="export-pdf"><FileText className="h-4 w-4 mr-1.5" />Export PDF report</Button>
              <Button variant="outline" onClick={() => window.open(`${API}/people/${oid}/report.csv`, "_blank")} data-testid="export-csv"><Download className="h-4 w-4 mr-1.5" />Export CSV</Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
